<script setup lang="ts">
import { computed, ref, type CSSProperties, type Ref } from 'vue';

interface Props {
  modelValue: string;
  placeholder?: string;
  disabled?: boolean;
  minRows?: number;
  maxHeight?: string;
  error?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  disabled: false,
  minRows: 3,
  maxHeight: '50vh',
  error: null,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const el: Ref<HTMLTextAreaElement | null> = ref(null);

const onInput = (event: Event) => {
  emit('update:modelValue', (event.target as HTMLTextAreaElement).value);
};

/** Элемент поля — для курсора и выделения (вставка emoji, авто-высота). */
defineExpose({ el });

/**
 * При `field-sizing: content` браузер игнорирует `rows` и схлопывает пустое
 * поле до одной строки, поэтому минимум строк держит `min-height`
 * (`lh` — высота строки поля, `1rem + 2px` — вертикальные паддинги и бордер).
 * `rows` остаётся запасным вариантом для движков без `field-sizing`.
 */
const inlineStyle = computed<CSSProperties>(() => ({
  fieldSizing: 'content',
  maxHeight: props.maxHeight,
  minHeight: `calc(${props.minRows}lh + 1rem + 2px)`,
  overflowY: 'auto',
  resize: 'none',
}));
</script>

<template>
  <textarea
    ref="el"
    :value="modelValue"
    :rows="minRows"
    :disabled="disabled"
    :placeholder="placeholder"
    :style="inlineStyle"
    class="w-full rounded-lg border bg-(--field-bg) px-3 py-2 text-sm text-(--text-primary) outline-none placeholder:text-(--text-secondary) disabled:opacity-50"
    :class="error ? 'border-(--danger)' : 'border-(--field-border) focus:border-(--accent)'"
    @input="onInput"
  ></textarea>
</template>
